import { isNumber, parseDate, round } from '@artup/utilities'
import { Assignable } from '../common'
import { Account } from '../accounts/account'
import { Payment } from './payment'
import { PaymentStatus } from './payment-status'
import { OrderItem } from './order-item'
import { OrderStatus } from './order-status'

/**
 * Order
 */
export class Order extends Assignable {
  constructor (data = {}) {
    super()
    this.assign(data, {
      createdAt: parseDate,
      updatedAt: parseDate,
      deletedAt: parseDate,
      isRecurring: Boolean
    })
  }

  normalize () {
    super.normalize()
    this.createdAt = this.createdAt || new Date()
    this.updatedAt = this.updatedAt || new Date()
    this.account = this.cast(this.account, Account)
    this.items = this.castArray(this.items, OrderItem, [])
    this.payment = this.cast(this.payment, Payment, new Payment())
    this.currency = (this.currency || 'eur').toString().toLowerCase()
    this.status = this.status || OrderStatus.Initialized
  }

  /**
   * Order identifier
   * @type {String}
   */
  id

  /**
   * Order creation date and time
   * @type {Date}
   */
  createdAt

  /**
   * Identifier of the account who created the order
   * @type {String}
   */
  createdBy

  /**
   * Account who created the order
   * @type {Account}
   */
  creator

  /**
   * Order update date and time
   * @type {Date}
   */
  updatedAt

  /**
   * Account who updated the order
   * @type {Account}
   */
  updater

  /**
   * Identifier of the account who updated the order
   * @type {String}
   */
  updatedBy

  /**
   * Order deletion date and time
   * @type {Date}
   */
  deletedAt

  /**
   * Identifier of the account who created the order
   * @type {String}
   */
  deletedBy

  /**
   * Order status
   * @type {OrderStatus}
   */
  status

  /**
   * Account who created the order
   * @type {Account}
   */
  account

  /**
   * Three-letter ISO currency code, in lowercase
   * @type {String}
   */
  currency

  /**
   * Summary description of the order
   * @type {String}
   */
  description

  /**
   * Order items
   * @type {OrderItem[]}
   */
  items

  /**
   * Identifier of a premium subscription to which the order is related, optional
   * @type {String}
   */
  premiumSubscriptionId

  /**
   * Indicates that the order should be recurring when subscription expires
   * @type {Boolean}
   */
  isRecurring

  /**
   * Payment details
   * @type {Payment}
   */
  payment

  /**
   * Total order value excluding VAT
   * @type {Number}
   */
  get netTotal () {
    const { items } = this
    if (items) {
      return round(items.reduce((total, item) => total + item.netTotal, 0), 2)
    } else {
      return 0
    }
  }

  /**
   * Total discount on the order value, excluding VAT
   * @type {Number}
   */
  get totalDiscount () {
    const { items } = this
    if (items) {
      return round(items.reduce((total, item) => total + item.totalDiscount, 0), 2)
    } else {
      return 0
    }
  }

  /**
   * Total sales tax on the order value
   * @type {Number}
   */
  get totalVAT () {
    const { items } = this
    if (items) {
      return round(items.reduce((total, item) => total + item.totalVAT, 0), 2)
    } else {
      return 0
    }
  }

  /**
   * Total order value including sales tax
   * @type {Number}
   */
  get total () {
    const { netTotal, totalVAT } = this
    return round(netTotal + totalVAT, 2)
  }

  /**
   * Determines whether VAT is applicable at all (which is different than VAT 0%)
   * @type {Boolean}
   */
  get isVATApplicable () {
    return (this.items || []).some(item => item.isVATApplicable)
  }

  /**
   * Checks whether customer's VAT number is from another EU country,
   * thus the customer is eligible for 0% VAT rate
   * on intracommunautaire deliveries
   * @param {String} sellerVATNumber Seller's VAT number
   * @returns {Boolean}
   */
  isZeroVATApplicable (sellerVATNumber) {
    const { customer } = this

    if (customer && sellerVATNumber && customer.VAT) {
      // Get country prefixes
      const customerCountry = customer.VAT.substring(0, 2).toUpperCase()
      const sellerCountry = sellerVATNumber.substring(0, 2).toUpperCase()

      // Applicable when different countries, handle situation when VAT without country was specified
      return customerCountry &&
        sellerCountry &&
        !isNumber(customerCountry) &&
        !isNumber(sellerCountry) &&
        customerCountry != sellerCountry
    }
  }

  /**
   * Applies the specified VAT rate on all order items.
   * Can be used for example to apply 0% VAT rate for VAT-registered
   * business customers from another EU country
   * @param {Number} rate VAT rate (percent)
   */
  applyVAT (rate) {
    for (const item of this.items || []) {
      item.VAT = rate
    }
  }

  /**
   * Makes sure that the status of the order is consistent
   * with the payment status
   * @returns {OrderStatus}
   */
  reconcileStatus () {
    const { payment } = this

    switch (payment?.status) {
      case PaymentStatus.Initiated:
        this.status = OrderStatus.Paying
        break

      case PaymentStatus.Completed:
        this.status = OrderStatus.Completed
        break

      case PaymentStatus.Cancelled:
      case PaymentStatus.Error:
        this.status = OrderStatus.Cancelled
        break

      default:
        this.status = OrderStatus.Initialized
    }

    return this.status
  }
}
